import type {
  ChannelFeedEngagement,
  ChannelFeedRelatedArtifacts,
} from "@mlx/contracts";
import { EllipsisIcon } from "lucide-react";
import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";

import {
  resolveFeedActor,
  sessionFeedActor,
} from "@/components/app/feed/feed-actor";
import type { FeedReactions } from "@/components/app/feed/feed-item-engagement";
import { FeedRecordChanges } from "@/components/app/feed/feed-record-details";
import {
  FeedRowView,
  FeedSentenceView,
  type FeedActorHoverComponent,
  type FeedContextChipComponent,
  type FeedRowDensity,
} from "@/components/app/feed/feed-row-view";
import {
  provenanceInterfaceType,
  sessionClientProvenance,
  type ProvenanceInterface,
} from "@/components/app/runs/provenance-avatar-stack";
import { contextDataSetHref } from "@/components/app/sessions/context-chip-view";
import { sessionStatus } from "@/components/app/sessions/session-status";
import { Button } from "@/components/ui/button";
import { artifactWorkspaceHref } from "@/lib/artifact-session-route";
import type {
  CurrentMlxUser,
  WorkspaceChannelFeedItem,
  WorkspaceChannelView,
  WorkspaceOrganizationMember,
  WorkspaceSessionChannelActivity,
} from "@/lib/mlx-workspace-client";
import { cn } from "@/lib/utils";
import { channelFeedRunArtifact } from "./channel-feed-artifact";
import {
  ChannelFeedFileResource,
  ChannelFeedResource,
} from "./channel-feed-resource";
import { feedActor } from "./channel-feed-shared";
import { channelViewHref } from "./channel-hrefs";
import { ChannelSessionArtifactLinks } from "./channel-session-artifact-links";

/*
 * Channel feed rows at rest, as markup, one kind at a time over the shared
 * row grammar. Inside a channel the channel chip is left off every sentence:
 * the reader is already there, and the cross-channel Feed page names it. A
 * thread renders its subject through the same row, read only. The rows in
 * channel-feed-row.tsx keep the viewer's reactions, the density, the cards,
 * a post's editing and the mention markdown, and render through these, so a
 * fixture item composes the same row. A post's and a discussion's text
 * arrives as `body`: the application renders it as mention markdown.
 */

/** The directory a row names its people from. */
type RowDirectory = {
  currentUser: CurrentMlxUser | null;
  membersByUserId: Map<string, WorkspaceOrganizationMember>;
};

/** What every kind of row takes besides its item. */
type RowCommon = RowDirectory & {
  channelId: string;
  showConnector: boolean;
  /** A thread's root: no engagement line, no actions, no hover tint. */
  readOnly?: boolean;
  density?: FeedRowDensity;
  /** The viewer's reactions; a row the viewer can react to shows react and its reactions. */
  reactions?: FeedReactions | null;
  onThreadOpen?: () => void;
  actorHover?: FeedActorHoverComponent;
};

/** A discussion's latest comment, clamped to three lines. */
export const CHANNEL_DISCUSSION_BODY_CLASS =
  "line-clamp-3 text-sm leading-6 text-foreground/90";

/** Any channel feed row, by the item's kind. */
export function ChannelFeedRowView({
  item,
  viewsById,
  body,
  editor,
  actions,
  footer,
  contextChip,
  ...common
}: RowCommon & {
  item: WorkspaceChannelFeedItem;
  viewsById: Map<string, WorkspaceChannelView>;
  /** A post's or a discussion's text, as markdown. */
  body?: ReactNode;
  /** A post being edited: the editor in place of its text. */
  editor?: ReactNode;
  /** Beside the time: a post's menu, or an attached session's removal. */
  actions?: ReactNode;
  /** After a post: its delete confirmation. */
  footer?: ReactNode;
  contextChip?: FeedContextChipComponent;
}) {
  if (item.type === "session")
    return (
      <ChannelSessionRowView
        {...common}
        activity={item.activity}
        engagement={item.engagement}
        relatedArtifacts={item.relatedArtifacts}
        viewsById={viewsById}
        actions={actions}
      />
    );
  if (item.type === "post")
    return (
      <ChannelPostRowView
        {...common}
        item={item}
        body={body}
        editor={editor}
        actions={actions}
        footer={footer}
      />
    );
  return (
    <ChannelFeedEntryRowView
      {...common}
      item={item}
      body={body}
      contextChip={contextChip}
    />
  );
}

/** A session that ran in the channel or was shared to it, with the views it touched and its artifacts. */
export function ChannelSessionRowView({
  activity,
  engagement,
  relatedArtifacts,
  channelId,
  currentUser,
  membersByUserId,
  viewsById,
  showConnector,
  readOnly = false,
  density,
  reactions = null,
  onThreadOpen,
  actions,
  actorHover,
}: RowCommon & {
  activity: WorkspaceSessionChannelActivity;
  engagement: ChannelFeedEngagement;
  relatedArtifacts: ChannelFeedRelatedArtifacts;
  viewsById: Map<string, WorkspaceChannelView>;
  /** Beside the time: removing an attached session's share, in the application. */
  actions?: ReactNode;
}) {
  const session = activity.session;
  const attachmentOnly =
    activity.reasons.includes("attached") &&
    activity.reasons.every((reason) => reason === "attached");
  const display = sessionFeedActor(session, currentUser, membersByUserId);
  const relatedViews = activity.viewIds.flatMap((viewId) => {
    const view = viewsById.get(viewId);
    return view ? [view] : [];
  });
  const visibleViews = relatedViews.slice(0, 3);
  const hiddenViewCount = Math.max(
    0,
    activity.viewIds.length - visibleViews.length,
  );
  const status =
    session.status && session.status !== "completed"
      ? sessionStatus(session.status).label
      : null;

  return (
    <FeedRowView
      actor={{ ...display, label: display.name }}
      provenance={{
        ...sessionClientProvenance(session.metadata),
        action: "started",
      }}
      occurredAt={activity.lastSeenAt}
      unread={!readOnly && engagement.unreadCount > 0}
      showConnector={showConnector}
      tone={readOnly ? "static" : "default"}
      density={density}
      sentence={
        <FeedSentenceView
          actor={attachmentOnly ? "Session" : display.name}
          actorIdentity={attachmentOnly ? null : display.identity}
          verb={attachmentOnly ? "added" : "ran"}
          subject={{
            label: session.title || "Untitled session",
            href: `/workspace/sessions/${encodeURIComponent(session.id)}`,
          }}
          status={status}
          trailing={
            visibleViews.length > 0 || hiddenViewCount > 0 ? (
              <>
                {visibleViews.map((view) => (
                  <span key={view.id}>
                    <ActivityDivider />
                    <Link
                      href={channelViewHref(channelId, view.id)}
                      className="underline-offset-4 hover:text-foreground hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {view.name}
                    </Link>
                  </span>
                ))}
                {hiddenViewCount > 0 ? (
                  <span>
                    <ActivityDivider />+{hiddenViewCount}{" "}
                    {hiddenViewCount === 1 ? "view" : "views"}
                  </span>
                ) : null}
              </>
            ) : null
          }
          actorHover={actorHover}
        />
      }
      footer={<ChannelSessionArtifactLinks related={relatedArtifacts} />}
      engagement={readOnly ? undefined : engagement}
      reactions={readOnly ? null : reactions}
      onThreadOpen={onThreadOpen}
      actions={actions}
    />
  );
}

function ActivityDivider() {
  return (
    <span
      aria-hidden
      className="mx-2 inline-block size-1 rounded-full bg-muted-foreground/40 align-middle"
    />
  );
}

/** A post: its author, when it was edited, and its text; the author's own posts carry a menu. */
export function ChannelPostRowView({
  item,
  currentUser,
  membersByUserId,
  showConnector,
  readOnly = false,
  density,
  reactions = null,
  onThreadOpen,
  body,
  editor,
  actions,
  footer,
  actorHover,
}: RowCommon & {
  item: Extract<WorkspaceChannelFeedItem, { type: "post" }>;
  /** The post's text, as markdown. */
  body?: ReactNode;
  /** The editor in place of the text while the author edits it. */
  editor?: ReactNode;
  /** Beside the time: the author's menu (ChannelPostActionsButton), in the application. */
  actions?: ReactNode;
  footer?: ReactNode;
}) {
  const actor = feedActor(
    item.post.authorId,
    item.post.authorType,
    currentUser,
    membersByUserId,
  );
  return (
    <FeedRowView
      actor={actor}
      provenance={{
        clientApp: item.post.clientApp,
        interfaceType: item.post.source === "agent" ? null : item.post.source,
        action: "posted",
      }}
      occurredAt={item.occurredAt}
      unread={!readOnly && item.engagement.unreadCount > 0}
      showConnector={showConnector}
      tone={readOnly ? "static" : "default"}
      density={density}
      actions={actions}
      sentence={
        <FeedSentenceView
          actor={actor.label}
          actorIdentity={actor.identity}
          trailing={
            item.post.updatedAt > item.post.createdAt ? (
              <span className="text-3xs text-muted-foreground">
                {" · Edited"}
              </span>
            ) : null
          }
          actorHover={actorHover}
        />
      }
      body={editor ?? <div className="text-foreground/90">{body}</div>}
      footer={footer}
      engagement={readOnly ? undefined : item.engagement}
      reactions={readOnly ? null : reactions}
      onThreadOpen={onThreadOpen}
    />
  );
}

/** The closed menu on the author's own post: shown on hover from sm up, always on a phone. */
export function ChannelPostActionsButton({
  className,
  ...props
}: Omit<ComponentProps<typeof Button>, "children" | "size" | "variant">) {
  return (
    <Button
      type="button"
      size="icon-xs"
      variant="ghost"
      title="Post actions"
      aria-label="Post actions"
      className={cn(
        "text-muted-foreground opacity-100 hover:text-foreground sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100",
        className,
      )}
      {...props}
    >
      <EllipsisIcon />
    </Button>
  );
}

/** A record, file, artifact or discussion row. */
export function ChannelFeedEntryRowView({
  item,
  channelId,
  currentUser,
  membersByUserId,
  showConnector,
  readOnly = false,
  density,
  reactions = null,
  onThreadOpen,
  body,
  actorHover,
  contextChip,
}: RowCommon & {
  item: Extract<
    WorkspaceChannelFeedItem,
    { type: "record" | "file" | "artifact" | "discussion" }
  >;
  /** A discussion's latest comment, as markdown styled by CHANNEL_DISCUSSION_BODY_CLASS. */
  body?: ReactNode;
  contextChip?: FeedContextChipComponent;
}) {
  const tone = readOnly ? "static" : "default";
  const unread = !readOnly && item.engagement.unreadCount > 0;
  const engagement = readOnly ? undefined : item.engagement;
  const rowReactions = readOnly ? null : reactions;
  if (item.type === "discussion") {
    const comment = item.discussion.latestComment;
    const actor = feedActor(
      comment.authorId,
      comment.authorType,
      currentUser,
      membersByUserId,
    );
    const interfaceType =
      comment.source === "agent"
        ? null
        : (comment.source as ProvenanceInterface);
    const thread = encodeURIComponent(
      `${item.discussion.subjectType}:${item.discussion.subjectId}`,
    );
    const href =
      `/workspace/channels/${encodeURIComponent(channelId)}` +
      `?view=feed&thread=${thread}&comment=${encodeURIComponent(comment.id)}`;
    return (
      <FeedRowView
        actor={actor}
        provenance={{
          clientApp: comment.clientApp,
          interfaceType,
          executedByTwin:
            comment.authorship?.author === "twin" ||
            comment.authorType === "agent",
          action: "posted",
        }}
        occurredAt={item.occurredAt}
        unread={unread}
        showConnector={showConnector}
        tone={tone}
        density={density}
        sentence={
          <FeedSentenceView
            actor={actor.label}
            actorIdentity={actor.identity}
            verb="commented on"
            subject={{ label: item.discussion.title, href }}
            actorHover={actorHover}
          />
        }
        body={body}
        engagement={engagement}
        reactions={rowReactions}
        onThreadOpen={onThreadOpen}
      />
    );
  }
  if (item.type === "record") {
    const display = resolveFeedActor(
      item.record.actor,
      membersByUserId,
      new Set(currentUser ? [currentUser.id] : []),
      currentUser,
    );
    const clientApp = item.record.actor.clientApp;
    const interfaceType = provenanceInterfaceType(
      item.record.actor.interfaceType,
    );
    const operation = item.record.operation;
    return (
      <FeedRowView
        actor={{ ...display, label: display.name }}
        provenance={{
          ...(clientApp === "claude-code" ||
          clientApp === "codex" ||
          clientApp === "gemini" ||
          clientApp === "chatgpt"
            ? { clientApp }
            : {}),
          ...(interfaceType ? { interfaceType } : {}),
          executedByTwin: item.record.actor.executedByTwin,
          action: operation === "created" ? "created" : "updated",
        }}
        occurredAt={item.occurredAt}
        unread={unread}
        showConnector={showConnector}
        tone={tone}
        density={density}
        sentence={
          <FeedSentenceView
            actor={display.name}
            actorIdentity={display.identity}
            verb={operation}
            subject={{
              label: item.record.title,
              href: `/workspace/channels/${encodeURIComponent(channelId)}?record=${encodeURIComponent(item.record.recordId)}&recordTab=discussion`,
            }}
            changes={
              operation !== "created" && item.record.changes.length > 0 ? (
                <FeedRecordChanges
                  details={item.record}
                  operation={operation}
                  currentUser={currentUser}
                  membersByUserId={membersByUserId}
                />
              ) : null
            }
            createdFieldCount={
              operation === "created"
                ? item.record.changes.length + item.record.hiddenChangeCount
                : 0
            }
            dataSet={
              item.record.dataSetName
                ? {
                    label: item.record.dataSetName,
                    href: contextDataSetHref(channelId, item.record.dataSetId),
                  }
                : null
            }
            actorHover={actorHover}
            contextChip={contextChip}
          />
        }
        engagement={engagement}
        reactions={rowReactions}
        onThreadOpen={onThreadOpen}
      />
    );
  }
  const actor =
    item.type === "file"
      ? feedActor(item.file.createdBy, "user", currentUser, membersByUserId)
      : feedActor(
          item.artifact.authorId ?? null,
          item.artifact.authorId ? "user" : "agent",
          currentUser,
          membersByUserId,
        );
  const artifact =
    item.type === "artifact" ? channelFeedRunArtifact(item.artifact) : null;
  const artifactVerb =
    item.type === "artifact" && item.artifact.operation === "updated"
      ? "updated"
      : "created";
  return (
    <FeedRowView
      actor={actor}
      provenance={{
        clientApp:
          item.type === "artifact" && !item.artifact.addedToChannel ? item.artifact.clientApp : undefined,
        interfaceType:
          item.type === "artifact" && !item.artifact.addedToChannel ? item.artifact.interfaceType : "web",
        action: artifactVerb,
      }}
      occurredAt={item.occurredAt}
      unread={unread}
      showConnector={showConnector}
      tone={tone}
      density={density}
      sentence={
        <FeedSentenceView
          actor={actor.label}
          actorIdentity={actor.identity}
          verb={
            item.type === "file"
              ? item.file.addedToChannel ? "added a file" : "uploaded a file"
              : item.artifact.addedToChannel ? `added ${item.artifact.kind === "csv" ? "a sheet" : item.artifact.kind === "markdown" ? "a document" : "a link"}` : `${artifactVerb} an artifact`
          }
          actorHover={actorHover}
        />
      }
      body={
        <>
          {item.type === "file" ? (
            <ChannelFeedFileResource file={item.file} />
          ) : artifact ? (
            <ChannelFeedResource
              href={artifact.contentKind ? artifactWorkspaceHref(artifact.id, artifact.sessionId) : artifact.href}
              title={item.artifact.title}
              kind={
                item.artifact.kind === "csv"
                  ? "spreadsheet"
                  : item.artifact.kind === "markdown"
                    ? "document"
                    : "link"
              }
              meta={
                artifact.contentKind
                  ? `${artifact.kindLabel} · v${item.artifact.version}${item.artifact.status === "proposed" ? " · awaiting review" : ""}`
                  : artifact.kindLabel
              }
            />
          ) : null}
          {item.type === "artifact" && item.artifact.sourceSessionId ? (
            <Link
              href={`/workspace/sessions/${encodeURIComponent(item.artifact.sourceSessionId)}`}
              className="mt-1.5 inline-block text-2xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              View source session
            </Link>
          ) : null}
        </>
      }
      engagement={engagement}
      reactions={rowReactions}
      onThreadOpen={onThreadOpen}
    />
  );
}
